import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import { headingsSchema } from '../Header';
import Modal from './Modal';

const schema: JSONSchema7 = {
    title: 'Modal',
    type: 'object',
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        openButtonHtml: {
            type: 'string',
            title: 'Open Button HTML',
            default: '<span>Open</span>',
        },
        closeButtonHtml: {
            type: 'string',
            title: 'Close Button HTML',
            default: '<span>Close</span>',
        },
        modalButtonAriaLabel: {
            type: 'string',
            title: 'Modal Button Aria Label',
            default: 'Open Modal',
        },
        showModalInEditMode: {
            type: 'boolean',
            title: 'Show modal children in edit mode',
            default: false,
        },
        titles: headingsSchema,
        showToolTip: {
            type: 'boolean',
            title: 'Show tooltip',
            default: false,
        },
        isShowPrintButton: {
            type: 'boolean',
            title: 'Show print button',
            default: false,
        },
        tooltipOptions: {
            title: 'Configure tooltip options',
            type: 'object',
            properties: {
                text: {
                    type: 'string',
                    title: 'Enter text',
                },
                textColor: {
                    type: 'string',
                    title: 'Enter text color',
                },
                backgroundColor: {
                    type: 'string',
                    title: 'Enter background color',
                },
            },
        },
        isWithinIframe: {
            type: 'boolean',
            title: 'Is within iframe',
            description: 'Position modal within iframe',
            default: false,
        },
    },
    dependencies: {
        isWithinIframe: {
            oneOf: [
                {
                    properties: {
                        isWithinIframe: { enum: [true] },
                        eventTypeName: {
                            type: 'string',
                            title: 'Event Type Name',
                            description: 'Specify the event type name',
                        },
                        topOffsetPercentage: {
                            type: 'string',
                            title: 'Top Offset Percentage',
                            description: 'Specify the top offset percentage',
                            default: '0.05',
                        },
                    },
                },
                {
                    properties: {
                        isWithinIframe: { enum: [false] },
                    },
                },
            ],
        },
        isShowPrintButton: {
            oneOf: [
                {
                    properties: {
                        isShowPrintButton: { enum: [true] },
                        printButtonHtml: {
                            type: 'string',
                            title: 'Close Button HTML',
                            default: 'Print',
                        },
                        printDocumentName: {
                            type: 'string',
                            title: 'Print Document Name',
                        },
                    },
                },
                {
                    properties: {
                        isShowPrintButton: { enum: [false] },
                    },
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'openButtonHtml',
            'closeButtonHtml',
            'modalButtonAriaLabel',
            'showModalInEditMode',
            'titles',
            'showToolTip',
            'tooltipOptions',
            'isWithinIframe',
            'eventTypeName',
            'topOffsetPercentage',
            'isShowPrintButton',
            'printButtonHtml',
            'printDocumentName',
        ],
    },
];

const uiSchema = {
    'ui:groups': groups,
    ...customSchemas.textSchema.TextSchema.uiSchema,
    ...customSchemas.styleSchema.StyleSchema.uiSchema,
    ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
    styles: {
        'ui:widget': 'CSSEditor',
    },
    titles: {
        items: {
            headerText: {
                'ui:widget': 'ResourcePicker',
            },
        },
    },
    openButtonHtml: {
        'ui:widget': 'ResourcePicker',
    },
    closeButtonHtml: {
        'ui:widget': 'ResourcePicker',
    },
    modalButtonAriaLabel: {
        'ui:widget': 'CustomTextInput',
    },
    showModalInEditMode: {
        'ui:widget': 'CustomCheckbox',
    },
    showToolTip: {
        'ui:widget': 'CustomCheckbox',
    },
    tooltipOptions: {
        text: {
            'ui:widget': 'CustomTextInput',
        },
        textColor: {
            'ui:widget': 'CustomTextInput',
        },
        backgroundColor: {
            'ui:widget': 'CustomTextInput',
        },
    },
    isWithinIframe: {
        'ui:widget': 'CustomCheckbox',
    },
    eventTypeName: {
        'ui:widget': 'CustomTextInput',
    },
    topOffsetPercentage: {
        'ui:widget': 'CustomTextInput',
    },
    isShowPrintButton: {
        'ui:widget': 'CustomCheckbox',
    },
    printButtonHtml: {
        'ui:widget': 'ResourcePicker',
    },
    printDocumentName: {
        'ui:widget': 'ResourcePicker',
    },
};

const ModalWidget: commonTypes.IWidget = {
    schema,
    uiSchema,
    templateView: Modal,
    id: 'Modal',
    group: 'component',
    contentPlaceHolders: ['modal'],
    initialData: () => ({
        openButtonHtml: '<span>Open</span>',
        closeButtonHtml: '<span>Close</span>',
        showModalInEditMode: false,
    }),
};

export default ModalWidget;
